import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import CampoDinheiro from '@/components/base/CampoDinheiro';
import ImagemProduto from '@/components/feature/ImagemProduto';
import type { CatalogoItem } from '../types';

interface Props {
  eventoId: number;
  pontoId: number;
  aoFechar: () => void;
  aoSalvo: () => void;
}

interface ItemEnvio {
  catalogo_id: number;
  preco_centavos: number;
  estoque_total?: number;
}

interface Selecao {
  preco: number;
  estoque: string;
}

export default function ModalCardapio({ eventoId, pontoId, aoFechar, aoSalvo }: Props) {
  const [catalogo, setCatalogo] = useState<CatalogoItem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [selecionados, setSelecionados] = useState<Record<number, Selecao>>({});
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      try {
        const { data, error } = await supabase.rpc('loja_catalogo_global');
        if (error) throw error;
        if (!ativo) return;
        setCatalogo((data as CatalogoItem[]) || []);
      } catch (e) {
        if (ativo) setErro(mensagemAmigavel(e));
      } finally {
        if (ativo) setCarregando(false);
      }
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, []);

  const grupos = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const filtrado = catalogo.filter(
      (c) =>
        !q ||
        c.nome.toLowerCase().includes(q) ||
        c.categoria_nome.toLowerCase().includes(q),
    );
    const ordem: string[] = [];
    const mapa = new Map<string, CatalogoItem[]>();
    for (const c of filtrado) {
      if (!mapa.has(c.categoria_nome)) {
        mapa.set(c.categoria_nome, []);
        ordem.push(c.categoria_nome);
      }
      mapa.get(c.categoria_nome)!.push(c);
    }
    return ordem.map((nome) => ({ nome, itens: mapa.get(nome)! }));
  }, [catalogo, busca]);

  const totalSelecionados = Object.keys(selecionados).length;

  function alternar(c: CatalogoItem) {
    setSelecionados((prev) => {
      const novo = { ...prev };
      if (novo[c.id]) delete novo[c.id];
      else novo[c.id] = { preco: 0, estoque: '' };
      return novo;
    });
  }

  function mudarPreco(id: number, preco: number) {
    setSelecionados((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], preco } } : prev));
  }

  function mudarEstoque(id: number, estoque: string) {
    setSelecionados((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], estoque } } : prev));
  }

  async function salvar() {
    const ids = Object.keys(selecionados).map(Number);
    if (ids.length === 0) return;
    const semPreco = ids.filter((id) => selecionados[id].preco <= 0);
    if (semPreco.length > 0) {
      setErro('Coloque o preço em todos os itens marcados.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const itens: ItemEnvio[] = ids.map((id) => {
        const s = selecionados[id];
        const item: ItemEnvio = { catalogo_id: id, preco_centavos: s.preco };
        const e = s.estoque.trim();
        if (e !== '') item.estoque_total = parseInt(e, 10);
        return item;
      });
      const { error } = await supabase.rpc('loja_adicionar_do_catalogo', {
        p_evento: eventoId,
        p_ponto: pontoId,
        p_itens: itens,
      });
      if (error) throw error;
      aoSalvo();
      aoFechar();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={aoFechar} aria-hidden />
      <div className="relative z-10 flex max-h-[92vh] w-full max-w-[640px] flex-col overflow-hidden rounded-t-3xl bg-white sm:rounded-3xl">
        <div className="flex flex-col items-center pt-2 sm:hidden">
          <span className="h-1.5 w-10 rounded-full bg-secondary-200" />
        </div>
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <div>
            <h3 className="text-[17px] font-bold text-foreground-950">Escolher do cardápio</h3>
            <p className="mt-0.5 text-[13px] text-foreground-500">
              Marque os itens, ponha o preço de cada um e (se quiser) o estoque inicial.
            </p>
          </div>
          <button
            type="button"
            onClick={aoFechar}
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
            aria-label="Fechar"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="border-b border-background-100 px-5 pb-3">
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-foreground-400">
              <i className="ri-search-line text-base" />
            </span>
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar item…"
              className="h-12 w-full rounded-2xl border border-secondary-300 bg-white pl-10 pr-4 text-[16px] text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {carregando ? (
            <div className="space-y-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-14 animate-pulse rounded-2xl bg-secondary-100" />
              ))}
            </div>
          ) : grupos.length === 0 ? (
            <p className="py-6 text-center text-[15px] text-foreground-500">
              Nenhum item encontrado com essa busca.
            </p>
          ) : (
            <div className="space-y-6">
              {grupos.map((g) => (
                <div key={g.nome}>
                  <h4 className="text-[13px] font-semibold uppercase tracking-wide text-foreground-500">
                    {g.nome}
                  </h4>
                  <div className="mt-2 space-y-2">
                    {g.itens.map((c) => {
                      const sel = selecionados[c.id];
                      return (
                        <div
                          key={c.id}
                          className={`rounded-2xl border p-3 transition-colors ${
                            sel ? 'border-primary-300 bg-primary-50/50' : 'border-secondary-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => alternar(c)}
                              aria-pressed={!!sel}
                              className={`flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-sm border-2 ${
                                sel ? 'border-primary-500 bg-primary-500' : 'border-secondary-400'
                              }`}
                            >
                              {sel && <i className="ri-check-line text-sm text-white" />}
                            </button>
                            <ImagemProduto
                              imagemUrl={null}
                              imagemSlug={c.imagem_slug}
                              emoji={c.emoji}
                              nome={c.nome}
                              tamanho="miniatura"
                              lazy
                            />
                            <span className="min-w-0 flex-1 truncate text-[15px] text-foreground-950">
                              {c.nome}
                            </span>
                            {c.unidade && (
                              <span className="shrink-0 text-[13px] text-foreground-400">
                                {c.unidade}
                              </span>
                            )}
                          </div>

                          {sel && (
                            <div className="mt-3 grid grid-cols-1 gap-3 pl-8 sm:grid-cols-2">
                              <CampoDinheiro
                                id={`loja-cat-preco-${c.id}`}
                                name={`preco-${c.id}`}
                                label="Preço"
                                centavos={sel.preco}
                                onChange={(v) => mudarPreco(c.id, v)}
                                required
                              />
                              <div>
                                <label
                                  htmlFor={`loja-cat-estoque-${c.id}`}
                                  className="block text-[14px] font-medium text-foreground-950"
                                >
                                  Estoque inicial
                                </label>
                                <input
                                  id={`loja-cat-estoque-${c.id}`}
                                  name={`estoque-${c.id}`}
                                  type="text"
                                  inputMode="numeric"
                                  value={sel.estoque}
                                  onChange={(e) =>
                                    mudarEstoque(c.id, e.target.value.replace(/[^0-9]/g, ''))
                                  }
                                  placeholder="vazio = não contar"
                                  className="mt-2 h-12 w-full rounded-2xl border border-secondary-300 bg-white px-4 text-[16px] text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {erro && (
          <div className="border-t border-background-100 bg-danger-50 px-5 py-3 text-[14px] text-danger-700">
            {erro}
          </div>
        )}

        <div className="border-t border-background-100 px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <span className="block text-center text-[13px] text-foreground-500">
            {totalSelecionados === 0
              ? 'Nenhum item marcado'
              : `${totalSelecionados} ${totalSelecionados === 1 ? 'item marcado' : 'itens marcados'}`}
          </span>
          <button
            type="button"
            onClick={salvar}
            disabled={salvando || totalSelecionados === 0}
            className="mt-2 flex min-h-[52px] w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl bg-primary-500 text-[15px] font-bold text-white shadow-lg shadow-primary-500/30 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {salvando
              ? 'Adicionando…'
              : `Adicionar ${totalSelecionados > 0 ? totalSelecionados : ''} ${totalSelecionados === 1 ? 'item' : 'itens'}`}
          </button>
          <button
            type="button"
            onClick={aoFechar}
            className="mx-auto mt-2 block cursor-pointer whitespace-nowrap text-[14px] font-medium text-foreground-500 transition-colors hover:text-foreground-800"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}